import {
  Box,
  Divider,
  Flex,
  Price,
  PrimaryButton,
  Spinner,
  Stack,
  Text,
} from "flicket-ui";
import isEmpty from "lodash/isEmpty";
import { ReactNode } from "react";
import { ContentModal } from "~components";
import { Icon, SlateRenderer } from "~components/common";
import { getAfterPayLocaleByCurrency } from "~config";
import { BNPLIntegration } from "~features/checkout/checkout.types";
import BuyNowPayLater from "~features/seated-reservation/components/common/common.BuyNowPayLater";
import {
  MembershipsSoldForUserQuery,
  ReferralRewardType,
  ReleaseType,
  TicketsSoldForUserQuery,
} from "~graphql/sdk";
import { isVisible } from "./non-seated.helpers";
import { ListItem } from "./non-seated.ListItem";
import {
  IReleaseTicket,
  TicketTypeType,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import {
  INonSeatedProps,
  TSoldOutWaitlistMessage,
  useViewModel,
} from "~features/nonseated-reservation/components/NonSeatedList/NonSeatedList.useViewModel";

const hasImportantNotice = (importantNotice?: string) =>
  importantNotice &&
  (JSON.parse(importantNotice) as any[]).some(({ children }) =>
    children.some(({ text }) => !isEmpty(text))
  );

type SelectedItem = IReleaseTicket & {
  quantity: number;
};

export const ReservationList = (props: INonSeatedProps) => {
  const {
    zones,
    isExperienceSales,
    delayedIsSubmitting,
    importantNotice,
    showIsR18,
    ticketTypeVariantTitle,
    hasResaleWaitlist,
    showHiddenFees,
    isAdmin,
    selectedTickets,
    selectedAddons,
    releaseType,
    requiresAuth,
    ticketsSoldForUser,
    SoldOutWaitlistMessage,
    showWaitlistMessage,
    addonTypes,
    multibuyDiscount,
    lineItemsTotal,
    activeReferralCampaign,
    hasTicketSelected,
    userCredits,
    user,
    totalPrice,
    isPOS,
    bnplGateways,
    stripeAfterpayGateway,
    isSubmitting,
    primaryButtonLabel,
    alertError,
    setAlertError,
    orgCurrency,
    isMobile,
    referralDiscount,
    events: { onChangeQuantity, onOrderCreationClick },
  } = useViewModel(props);

  if (delayedIsSubmitting) {
    return <DelayedSubmissionMessage />;
  }

  return (
    <Box
      maxW={{ _: "100%", md: isExperienceSales ? "920px" : "640px" }}
      m={{ _: 0, md: "0 auto" as any }}
      px={2}
    >
      {hasImportantNotice(importantNotice) && !isPOS && (
        <Box mb={showIsR18 ? 2 : 5}>
          <ImportantNotice value={importantNotice} />
        </Box>
      )}

      {showIsR18 && <R18Notice />}

      <Section
        isExperienceSales={isExperienceSales}
        title={`Select ${ticketTypeVariantTitle}`}
      >
        <ZoneList
          isExperienceSales={isExperienceSales}
          zones={zones}
          hasResaleWaitlist={hasResaleWaitlist}
          showHiddenFees={showHiddenFees}
          onChange={onChangeQuantity}
          isAdmin={isAdmin}
          selectedTickets={selectedTickets}
          releaseType={releaseType}
          requiresAuth={requiresAuth}
          ticketsSoldForUser={ticketsSoldForUser}
          SoldOutWaitlistMessage={SoldOutWaitlistMessage}
          showWaitlistMessage={showWaitlistMessage}
        />
      </Section>

      {addonTypes.length > 0 && (
        <Section isExperienceSales={isExperienceSales} title={"Select Addons"}>
          {!isExperienceSales && <Divider mb={{ _: 2, md: 1 }} />}

          {addonTypes.map((addonType) => {
            const selectedQuantity = selectedAddons?.find(
              (addon) => addon.id === addonType.id && addon.zoneId === "no-zone"
            );

            return (
              <ListItem
                key={addonType.id}
                ticketType={addonType}
                handleChange={(
                  id: string,
                  quantity: number,
                  type: TicketTypeType
                ) =>
                  onChangeQuantity(
                    id,
                    quantity,
                    type,
                    "no-zone",
                    addonType.price
                  )
                }
                quantity={selectedQuantity?.quantity ?? 0}
              />
            );
          })}
        </Section>
      )}

      <Divider mt={{ _: 3, md: 0 }} />

      {multibuyDiscount > 0 && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Sub-Total
            </Text>
            <Price
              price={lineItemsTotal}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            />
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Multibuy promotion applied
            </Text>
            <Price
              price={-multibuyDiscount}
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            />
          </Flex>
          <Divider my={{ _: 3, md: 2 }} />
        </>
      )}

      {activeReferralCampaign &&
        activeReferralCampaign.referralUserRewardType ===
          ReferralRewardType.Discount && (
          <>
            <Flex mt={2} justifyContent="space-between" w="100%">
              <Text fontWeight="medium" fontSize={2} color="N600">
                Sub-Total
              </Text>
              <Price
                price={lineItemsTotal}
                fontWeight="medium"
                fontSize={4}
                lineHeight={"18px" as any}
                color={"black" as any}
              />
            </Flex>
            <Flex mt={2} justifyContent="space-between" w="100%">
              <Text fontWeight="medium" fontSize={2} color="N600">
                Referral discount applied
              </Text>
              <Price
                price={-referralDiscount}
                fontWeight="medium"
                fontSize={4}
                lineHeight={"18px" as any}
                color={"black" as any}
              />
            </Flex>
            <Divider my={{ _: 3, md: 2 }} />
          </>
        )}

      <Flex mt={2} justifyContent="space-between" w="100%">
        <Text fontWeight="medium" fontSize={2} color="N600">
          Total {showHiddenFees ? "(including booking fees and taxes)" : ""}
        </Text>
        <Text
          fontWeight="medium"
          fontSize={4}
          lineHeight={"18px" as any}
          color={"black" as any}
        >
          {hasTicketSelected ? <Price price={totalPrice} /> : "-"}
        </Text>
      </Flex>

      {userCredits > 0 && hasTicketSelected && (
        <>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Your credits to be deducted
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {`- $${user.credits.toFixed(2)}`}
            </Text>
          </Flex>
          <Flex mt={2} justifyContent="space-between" w="100%">
            <Text fontWeight="medium" fontSize={2} color="N600">
              Total left to pay after deducting credits
            </Text>
            <Text
              fontWeight="medium"
              fontSize={4}
              lineHeight={"18px" as any}
              color={"black" as any}
            >
              {totalPrice - userCredits > 0 ? (
                <Price price={totalPrice - userCredits} />
              ) : (
                "$0.00"
              )}
            </Text>
          </Flex>
        </>
      )}

      {!isPOS && totalPrice - userCredits > 0 && (
        <>
          {bnplGateways.length > 0 &&
            bnplGateways.map((g) => (
              <Box mt={2} w="100%" key={g.id}>
                <BuyNowPayLater
                  totalPrice={totalPrice - userCredits}
                  hasTicketSelected={hasTicketSelected}
                  showHiddenFees={showHiddenFees}
                  isSmallDisplay={isMobile}
                  bnplIntegration={g as BNPLIntegration} // This was checked when filtering.
                />
              </Box>
            ))}
          {stripeAfterpayGateway && (
            <Flex flexDirection={"row-reverse"}>
              {/* @ts-expect-error - Afterpay placement component */}
              <afterpay-placement
                data-locale={getAfterPayLocaleByCurrency(orgCurrency)}
                data-currency={orgCurrency ?? "NZD"}
                data-amount={totalPrice - userCredits}
              >
                {/* @ts-expect-error - Afterpay placement component */}
              </afterpay-placement>
            </Flex>
          )}
        </>
      )}

      <PrimaryButton
        mt={{ _: 2, md: 6 }}
        padding="mobileGutter"
        w="100%"
        onClick={onOrderCreationClick}
        isLoading={isSubmitting}
        disabled={hasTicketSelected === false}
      >
        {primaryButtonLabel}
      </PrimaryButton>
      {/* DON"T REMOVE: 
       When youre on safari the buy tickets button is hidden as the nav bar is at the bottom. 
       We might be able to add bottom padding or use dvh css property */}
      <br />
      <br />
      <br />
      <br />
      <br />

      <ContentModal
        isOpen={!!alertError}
        setIsOpen={() => setAlertError(undefined)}
        title={alertError?.title}
      >
        {typeof alertError?.message === "string" ? (
          <div dangerouslySetInnerHTML={{ __html: alertError?.message }} />
        ) : (
          alertError?.message
        )}
      </ContentModal>
    </Box>
  );
};

function Section({
  isExperienceSales,
  title,
  children,
}: {
  isExperienceSales: boolean;
  title: ReactNode;
  children?: ReactNode;
}) {
  const TitleComponent = (() => {
    if (isExperienceSales) {
      return <Text variant="header.M">{title}</Text>;
    }

    return (
      <Text
        color="N800"
        fontWeight="heavy"
        fontSize={{ _: 6, md: 7 }}
        textAlign={{ _: "center", md: "center" }}
        lineHeight={{ _: "normal", md: "high" }}
        marginTop={{ _: 3, md: 0 }}
      >
        {title}
      </Text>
    );
  })();

  return (
    <Stack gap={isExperienceSales ? 3 : 2} direction="vertical" mb={3}>
      {TitleComponent}
      {children}
    </Stack>
  );
}

function Group({
  isExperienceSales,
  title,
  description,
  children,
}: {
  isExperienceSales: boolean;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
}) {
  const TitleComponent = (() => {
    if (isExperienceSales) {
      return (
        <Box>
          <Text variant="header.M">{title}</Text>
          {description && <Text variant="regular">{description}</Text>}
        </Box>
      );
    }

    return (
      <Box>
        <Text
          color="N800"
          fontWeight="heavy"
          fontSize={{ _: 6, md: 7 }}
          textAlign={{ _: "left", md: "left" }}
          lineHeight="normal"
        >
          {title}
        </Text>
        {description && (
          <Box
            w="100%"
            p={2}
            bg="N100"
            borderRadius="sm"
            border="1px"
            borderColor="N300"
            mt={2}
          >
            <Text
              color="N800"
              fontSize={3}
              textAlign={{ _: "left", md: "left" }}
              lineHeight="medium"
            >
              {description}
            </Text>
          </Box>
        )}
      </Box>
    );
  })();

  return (
    <Stack gap={isExperienceSales ? 2 : 0} direction="vertical">
      {title && TitleComponent}
      {children}
    </Stack>
  );
}

function ZoneList({
  zones,
  releaseType,
  requiresAuth,
  ticketsSoldForUser,
  selectedTickets,
  showWaitlistMessage,
  isAdmin,
  onChange,
  showHiddenFees,
  SoldOutWaitlistMessage,
  isExperienceSales,
}: {
  onChange: (
    id: string,
    value: number,
    type: TicketTypeType,
    zoneId: string,
    price: number
  ) => void;
  zones: IReleaseZone[];
  isExperienceSales: boolean;
  isAdmin: boolean;
  requiresAuth: boolean;
  hasResaleWaitlist: boolean;
  selectedTickets: SelectedItem[];
  releaseType: ReleaseType;
  showHiddenFees: boolean;
  ticketsSoldForUser:
    | TicketsSoldForUserQuery["ticketsSoldForUser"]
    | MembershipsSoldForUserQuery["membershipsSoldForUser"];
  SoldOutWaitlistMessage: TSoldOutWaitlistMessage;
  showWaitlistMessage: boolean;
}) {
  // this value has derived from the zone ticketTypes which uses a ResolvedField see backend for more info.
  const soldOutZones = zones?.filter((zone) =>
    zone.ticketTypes
      ?.filter((tt) => tt.isActive)
      ?.every((tt) => tt.isSoldOut ?? false)
  );

  const list = zones
    ?.map((zone) => {
      if (!zone.isActive) {
        return null;
      }

      const ticketListItems = zone.ticketTypes
        .filter((ticketType) => {
          if (!ticketType.isActive && releaseType !== ReleaseType.Presale) {
            return false;
          }

          if (
            !isVisible(selectedTickets, ticketType, ticketsSoldForUser) &&
            !isAdmin
          ) {
            return false;
          }

          return true;
        })
        .map((ticketType) => {
          const selectedQuantity = selectedTickets?.find(
            (seat) => seat.id === ticketType.id && seat.zoneId === zone?.name
          );

          return (
            <ListItem
              key={`${zone?.name}-${ticketType.id}`}
              ticketType={ticketType}
              showHiddenFees={showHiddenFees}
              handleChange={(
                id: string,
                quantity: number,
                type: TicketTypeType
              ) => onChange(id, quantity, type, zone?.name, ticketType.price)}
              quantity={selectedQuantity?.quantity ?? 0}
            />
          );
        });

      if (zone.description || ticketListItems.length > 0) {
        return (
          <Group
            isExperienceSales={isExperienceSales}
            key={zone?.name}
            title={zone.name}
            description={zone.descriptionShort}
          >
            <>
              {!isExperienceSales && <Divider my={{ _: 2, md: 2 }} />}

              <Box>{ticketListItems}</Box>

              {showWaitlistMessage &&
                (() => {
                  const soldOutZone = soldOutZones?.find(
                    (z) => z.id === zone.id
                  );

                  const preventWaitlistJoining =
                    (requiresAuth && releaseType === ReleaseType.Unlisted) ||
                    releaseType === ReleaseType.Pos ||
                    releaseType === ReleaseType.Outlet;

                  if (!soldOutZone || preventWaitlistJoining) return null;

                  return (
                    <>
                      <Divider my={{ _: 2, md: 2 }} />
                      <SoldOutWaitlistMessage zoneName={soldOutZone.name} />
                    </>
                  );
                })()}
            </>
          </Group>
        );
      }

      return null;
    })
    .filter((v) => v);

  return <>{list}</>;
}

function DelayedSubmissionMessage() {
  return (
    <Box mx={"1/2"} my={20} px={4}>
      <Spinner size={64} />
      <Text
        mt={4}
        fontSize={4}
        lineHeight="high"
        color="N600"
        textAlign="center"
      >
        We&apos;re just double-checking that you&apos;re not a ticket-buying
        robot in disguise.
      </Text>
      <Text
        mt={1}
        fontSize={4}
        lineHeight="high"
        color="N600"
        textAlign="center"
      >
        {" "}
        Your tickets are nearly ready.
      </Text>
      <Text
        textAlign="center"
        mt={4}
        fontSize={4}
        lineHeight="high"
        color="N600"
        fontWeight={"heavy"}
      >
        Don&apos;t refresh this page.
      </Text>
    </Box>
  );
}

function ImportantNotice({ value }: { value: string }) {
  return (
    <Box
      w="100%"
      p={2}
      bg="N100"
      borderRadius="sm"
      border="1px"
      borderColor="N300"
    >
      <Flex mb={3}>
        <Icon icon="info" color="N600" fontSize={6} />
        <Text
          fontSize={6}
          lineHeight="normal"
          fontWeight="heavy"
          color="N800"
          ml={1}
        >
          Important notes
        </Text>
      </Flex>
      <SlateRenderer value={value} />
    </Box>
  );
}

function R18Notice() {
  return (
    <Flex
      alignItems="center"
      borderRadius="sm"
      backgroundColor="error25"
      px="6/4"
      py={2}
      mb={5}
    >
      <Icon icon="r18" fontSize={6} mr={1} />
      <Text fontWeight="medium" fontSize={2} lineHeight="medium" color="N700">
        This is an R18 event
      </Text>
    </Flex>
  );
}
